import { MouseEvent } from 'react';
import { Button, ButtonProps } from '@shared/components/Button';
import { useMenuContext } from './MenuContext';

export type MenuButtonProps = Partial<Omit<ButtonProps, 'arrow'>> & {
  showDownArrow?: boolean;
};

/**
 * Menu.Button
 *
 * A pre-styled trigger button that toggles the menu. Shows an optional
 * down-arrow indicator and an active state while the menu is open, and hides
 * its tooltip until the menu closes. Use this when you want a standard button
 * trigger; use `Menu.Trigger` when you need to wrap a custom element instead.
 */

export function MenuButton({
  variant = 'select',
  iconOnly,
  showDownArrow = !iconOnly,
  style,
  tooltipDisabled,
  onClick: onClickProp,
  ...props
}: MenuButtonProps) {
  const { toggle, isOpen, anchorName, popoverId } = useMenuContext();

  const onClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    await onClickProp?.(event);
    if (event.defaultPrevented) {
      return;
    }
    toggle();
  };

  const mergedProps = {
    variant,
    iconOnly,
    arrow: showDownArrow ? 'down' : undefined,
    ...props,
  } as ButtonProps;

  return (
    <Button
      {...mergedProps}
      style={{ anchorName, ...style }}
      tooltipDisabled={isOpen || tooltipDisabled}
      onClick={onClick}
      popovertarget={popoverId}
      popovertargetaction="show"
      aria-haspopup="menu"
      aria-expanded={isOpen || undefined}
    />
  );
}
